import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Tooltip, Button } from 'antd';
import cn from 'classnames';
import { Filter } from './widgets/filter';
import { ShutdownsTable } from './widgets/shutdownsTable';
import { toggleFilterVisble, setDefaultFilter } from './features/filter';
import {
  changeShutdownDesc,
  changeShutdownAlisaText,
  changeShutdownAddresses,
  changeShutdownPlanEndDate,
  changeShutdownEndDate,
  changeShutdownType,
  changeShutdownReason,
  changeShutdownInitiators,
  changeShutdownResponsible,
  changeShutdownNotifyTo,
  showShutdownHistory,
  createShutdownNew,
} from './features/shutdown';
import { DownloadShutdownReports } from './features/downloadReports';
import { NewShutdownAlert } from './features/newShutdownAlert';
import { ToggleModalsVisibleProvider } from './shared/lib/toggleModalVisble';
import { AlisaAudioManagerProvider } from './shared/lib/alisaAudioManager';
import useSetRefreshFn from '../../hooks/useSetRefreshFn';
import {
  ShutdownFromNotificationsProvider,
  ShutdonwFromNotificationsAlert,
} from './features/showAndResetFromNotificationsAlert';
import {
  ResponseTemplateModal,
  ResponseTemplateModalProvider,
  OpenResponseTemplateModal,
  DeleteTemplateBtn,
} from './features/autoResponseTemplate';
import styles from './Shutdowns.module.scss';
import {
  OpenFilterNewWithActions,
  Filter as FilterNew,
} from './widgets/filterNew';
import { useDispatch, useSelector } from 'react-redux';
import { fetchShutdownsThunk } from './shared/model/actions';
import useDefaultFilterQuery from './shared/api/queries/useDefaultFilterQuery';
import useChooseFilter from './shared/hooks/useChooseFilter';
import { SearchNewShutdown } from './features/filterNew/search';

const { useSetDefaultFilter } = setDefaultFilter;
const { FilterVisibleProvider } = toggleFilterVisble;
const { CreateShutdownModalNew, OpenCreateShutdownModalNew } =
  createShutdownNew;
const { ChangeShutdownDescModal } = changeShutdownDesc;
const { ChangeShutdownAlisaTextModal } = changeShutdownAlisaText;
const { ChangeShutdownAddressesModal } = changeShutdownAddresses;
const { ChangeShutdownPlanEndDateModal } = changeShutdownPlanEndDate;
const { ChangeShutdownEndDateModal } = changeShutdownEndDate;
const { ChangeShutdownTypeModal } = changeShutdownType;
const { ChangeShutdownReasonModal } = changeShutdownReason;
const { ChangeShutdownInitiatorsModal } = changeShutdownInitiators;
const { ChangeShutdownResponsibleModal } = changeShutdownResponsible;
const { ChangeShutdownNotifyToModal } = changeShutdownNotifyTo;
const { ShutdownHistoryModal } = showShutdownHistory;

const renderAddResTemplateAction = () => (
  <OpenResponseTemplateModal
    render={({ onClick }) => (
      <Tooltip title="Добавить шаблон">
        <Button
          shape="circle"
          icon="plus"
          onClick={() => onClick()}
        />
      </Tooltip>
    )}
  />
);

const renderResTemplateMutations = ({ item, actionClassName }) => (
  <>
    <OpenResponseTemplateModal
      render={({ onClick }) => (
        <Tooltip title="Редактировать шаблон">
          <Button
            shape="circle"
            icon="edit"
            className={actionClassName}
            onClick={(e) => {
              e.stopPropagation();
              onClick(item);
            }}
          />
        </Tooltip>
      )}
    />
    <DeleteTemplateBtn
      item={item}
      render={() => (
        <Tooltip title="Удалить шаблон">
          <Button
            shape="circle"
            icon="minus"
            className={actionClassName}
            onClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
      )}
    />
  </>
);

const ShutdownsContainer = () => {
  const dispatch = useDispatch();

  const { data: defaultFilter, isFetching: isFetchingDefaultFilter } =
    useDefaultFilterQuery();

  const chooseFilter = useChooseFilter();

  const { data: shutdowns, loading: isFetchingShutdowns } = useSelector(
    (state) => state.shutdownStore
  );

  const isFetching = isFetchingShutdowns || isFetchingDefaultFilter;

  useSetRefreshFn(() => {
    dispatch(fetchShutdownsThunk());
  }, [dispatch]);

  useEffect(() => {
    const run = async () => {
      if (!!defaultFilter) {
        await chooseFilter(defaultFilter.id);

        dispatch(fetchShutdownsThunk());
      } else {
        dispatch(fetchShutdownsThunk());
      }
    };

    run();
  }, [defaultFilter, dispatch]);

  return (
    <>
      <section className={styles.root}>
        <ShutdonwFromNotificationsAlert
          loading={isFetching}
          hasData={
            Array.isArray(shutdowns)
              ? Boolean(shutdowns.length)
              : Boolean(shutdowns)
          }
        />
        <NewShutdownAlert />
        <div className={cn(styles.header, styles.wrapper)}>
          <OpenCreateShutdownModalNew
            render={({ open }) => (
              <Tooltip
                title="Создать отключение"
                placement="bottom"
              >
                <Button
                  type="primary"
                  shape="circle"
                  icon="plus"
                  onClick={open}
                />
              </Tooltip>
            )}
          />
          <SearchNewShutdown />
          <DownloadShutdownReports />
          <OpenFilterNewWithActions />
        </div>
        <ShutdownsTable
          shutdowns={
            !shutdowns || Array.isArray(shutdowns)
              ? [...(shutdowns ?? [])].reverse()
              : [shutdowns]
          }
          loading={isFetching}
          className={styles.wrapper}
        />
      </section>
      <Filter />
      <FilterNew />

      <ResponseTemplateModalProvider>
        <CreateShutdownModalNew
          renderResTemplateAction={renderAddResTemplateAction}
          renderResTemplateOptionActions={renderResTemplateMutations}
        />
        <ChangeShutdownAlisaTextModal
          renderAction={renderAddResTemplateAction}
          renderOptionActions={renderResTemplateMutations}
        />
        <ResponseTemplateModal />
      </ResponseTemplateModalProvider>

      <ChangeShutdownDescModal />
      <ChangeShutdownAddressesModal />
      <ChangeShutdownPlanEndDateModal />
      <ChangeShutdownEndDateModal />
      <ChangeShutdownTypeModal />
      <ChangeShutdownReasonModal />
      <ChangeShutdownInitiatorsModal />
      <ChangeShutdownResponsibleModal />
      <ShutdownHistoryModal />
      <ChangeShutdownNotifyToModal />
    </>
  );
};

const Shutdowns = () => {
  const location = useLocation();
  const history = useHistory();

  const [locationState, setLocationState] = useState(
    location.state?.from === 'notifications' ? location.state : null
  );

  const setDefaultFilter = useSetDefaultFilter();

  useEffect(() => {
    if (location.state?.from === 'notifications') {
      setLocationState(location.state);
      history.replace({ state: undefined });
    }
  }, [location.state]);

  useEffect(() => {
    setDefaultFilter(locationState);
  }, [locationState]);

  return (
    <ShutdownFromNotificationsProvider
      state={locationState}
      setState={setLocationState}
    >
      <FilterVisibleProvider>
        <ToggleModalsVisibleProvider>
          <AlisaAudioManagerProvider>
            <ShutdownsContainer />
          </AlisaAudioManagerProvider>
        </ToggleModalsVisibleProvider>
      </FilterVisibleProvider>
    </ShutdownFromNotificationsProvider>
  );
};

export default Shutdowns;
